import * as React from "react";
import { Button, Form, Input, Radio, Space } from "antd";
import { ModalPlm } from "src/components/ModalPlm";
import {
  DeleteOutlined,
  InsertRowAboveOutlined,
  InsertRowBelowOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import { trimRequired } from "src/common/validationRules/trimRequired";
import { Rule } from "antd/es/form";
import { NamePath } from "antd/es/form/interface";
import { EntityFiltersPageStore } from "src/pages/EntityFiltersPage/EntityFiltersPageStore";
import { multiStepOnRowClick } from "src/components/MultiStepTask/utils";
import { ZEntityRow } from "src/pages/EntityFiltersPage/EntityList/types";
import { ZEntity } from "src/types/ZEntity";
import { onError } from "src/common/onError";
import { ZMc2Settings } from "../../../Mc2Types";
import { Mc2SelectSizeLineName } from "./Mc2SelectSizeLineName";
import { Mc2MapPoint } from "../Mc2EntityTypes";
import { Mc2SizeMapData } from "./Mc2SizeMapData";
import styles from "./Mc2EntitySizeLineModal.module.less";

/* eslint no-plusplus: "off" */
/* eslint react/no-unstable-nested-components: "off" */

interface PropsMc2EntitySizeLineModal {
  open: boolean;
  close: () => void;
  initialData: Mc2SizeMapData;
  settings: ZMc2Settings;
  onChange: (data: Mc2SizeMapData) => Promise<void>;
  saving: boolean;
  sizeLineAttrNameId: string;
}

export const Mc2EntitySizeLineModal: React.FC<PropsMc2EntitySizeLineModal> = (
  props,
) => {
  const {
    open,
    close,
    initialData,
    settings,
    onChange,
    saving,
    sizeLineAttrNameId,
  } = props;
  const [form] = Form.useForm<Mc2SizeMapData>();
  const dstName = Form.useWatch("dstName", form);
  const [baseIndex, setBaseIndex] = React.useState(0);
  const updateBaseIndex = (data: Mc2SizeMapData) => {
    setBaseIndex(data.points.findIndex(({ baseSize }) => baseSize));
  };
  const [sizeLineStore, setSizeLineStore] =
    React.useState<EntityFiltersPageStore | null>(null);

  React.useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue(initialData);
      updateBaseIndex(initialData);
    }
  }, [open]);
  const selectNewName = () => {
    const onRowClick = (row: ZEntityRow) => {
      multiStepOnRowClick(row, slStore);
    };
    const slStore = new EntityFiltersPageStore({
      objectId: settings.objSizeLineId!,
      selectionSettings: { selectionType: "radio" },
      onRowClick,
      actions: new Set(), // Чтобы не было кнопки создания модели, т.к. она вызывает переход на другую страницу.
    });
    setSizeLineStore(slStore);
  };
  const onChangeSizeLine = (row: ZEntity) => {
    const nameAttr = row.attributeValues.find(
      ({ attributeId }) => attributeId === sizeLineAttrNameId,
    );
    if (nameAttr) {
      form.setFieldValue("dstName", nameAttr.values?.[0]);
      form.setFieldValue("dstId", row.id);
    } else {
      onError(Error("Не найден атрибут названия"));
    }
  };
  return (
    <>
      <ModalPlm
        title="Изменение размерного ряда"
        open={open}
        onCancel={close}
        centered
        okText="Изменить"
        width="700px"
        height="90vh"
        okButtonProps={{
          htmlType: "submit",
          loading: saving,
        }}
        modalRender={(content) => (
          <Form<Mc2SizeMapData>
            form={form}
            layout="vertical"
            style={{ height: "100%" }}
            onValuesChange={(changed: Partial<Mc2SizeMapData>, all) => {
              updateBaseIndex(all);
            }}
            onFinish={(values: Mc2SizeMapData) => {
              onChange(transformData(values)).then(close).catch(onError);
            }}
          >
            {content}
          </Form>
        )}
      >
        <div className={styles.content}>
          <div className={styles.topLine}>
            <Form.Item name="dstId" hidden />
            <Form.Item label="Текущий размерный ряд" name="srcName">
              <Input disabled />
            </Form.Item>
            <Form.Item
              label="Новый размерный ряд"
              name="dstName"
              rules={[trimRequired()]}
            >
              <Space.Compact style={{ width: "100%" }}>
                <Input disabled value={dstName} />
                <Button
                  type="primary"
                  icon={<PlusCircleOutlined />}
                  onClick={selectNewName}
                />
              </Space.Compact>
            </Form.Item>
          </div>
          <div className={styles.listBox}>
            <Form.List name="points">
              {(fields, { add, remove }) => (
                <>
                  <div className={styles.listTools}>
                    <div className={styles.toolsLabel}>
                      Соответствие размеров
                    </div>
                    <Button
                      icon={<InsertRowAboveOutlined />}
                      onClick={() => add(makePoint(), 0)}
                    >
                      Добавить в начало
                    </Button>
                    <Button
                      icon={<InsertRowBelowOutlined />}
                      onClick={() => add(makePoint())}
                    >
                      Добавить в конец
                    </Button>
                  </div>
                  <div className={styles.listContent}>
                    <div className={styles.table}>
                      <div className={styles.th}>Текущий</div>
                      <div className={styles.th}>Базовый</div>
                      <div className={styles.th}>Новый</div>
                      <div className={styles.th} />
                      {fields.map(({ key, name }) => (
                        <React.Fragment key={key}>
                          <Form.Item
                            name={[name, "nameFrom"]}
                            className={styles.td}
                          >
                            <Input readOnly disabled />
                          </Form.Item>
                          <Form.Item
                            name={[name, "baseSize"]}
                            className={styles.td}
                            valuePropName="checked"
                          >
                            <Radio
                              onChange={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const points: Mc2MapPoint[] =
                                  form.getFieldValue("points");
                                points.forEach((_, i) => {
                                  form.setFieldValue(
                                    ["points", i, "baseSize"],
                                    name === i,
                                  );
                                });
                              }}
                            />
                          </Form.Item>
                          <Form.Item
                            name={[name, "nameTo"]}
                            rules={[trimRequired(), ruleUnique(name)]}
                            className={styles.td}
                            dependencies={dstPathList(fields.length)}
                          >
                            <Input
                              style={{ textTransform: "uppercase" }}
                              onPressEnter={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                if (name + 1 < fields.length) {
                                  document
                                    .getElementById(`points_${name + 1}_nameTo`)
                                    ?.focus();
                                }
                              }}
                            />
                          </Form.Item>
                          <div className={styles.td}>
                            <Button
                              icon={<DeleteOutlined />}
                              onClick={() => remove(name)}
                              disabled={name === baseIndex}
                            />
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </Form.List>
          </div>
        </div>
      </ModalPlm>
      <Mc2SelectSizeLineName
        store={sizeLineStore}
        close={() => setSizeLineStore(null)}
        success={(selected) => {
          onChangeSizeLine(selected as ZEntity);
        }}
      />
    </>
  );
};

const dstPathList = (count: number): NamePath[] => {
  const res: NamePath[] = [];
  for (let i = 0; i < count; i++) {
    res.push(["points", i, "nameTo"]);
  }
  return res;
};

const ruleUnique =
  (name: number): Rule =>
  (form) => ({
    validator: (_, value) => {
      if (value) {
        const points: Mc2MapPoint[] = form.getFieldValue("points");
        const dup = points.find(
          ({ nameTo }, i) => nameTo === value && i !== name,
        );
        if (dup) return Promise.reject(Error("одинаковые имена"));
      }
      return Promise.resolve();
    },
  });

const makePoint = (): Mc2MapPoint => ({
  nameFrom: "",
  baseSize: false,
  nameTo: "",
});

// К сожалению, Ant Input не позволяет трансформировать вводимый текст на лету.
// Рекомендации в инете по onInput создают проблему, т.к. курсор в этом случае переносится в конец.
// А ещё и контролировать курсор - это черезмерное усложнение, снижающее надёжность.
// Поэтому найдено обходное решение из двух пунктов:
// 1. Имитировать CapsLock для строки ввода через CSS
// 2. Вызывать toUpperCase для данных в момент сабмита формы
const transformData = (src: Mc2SizeMapData): Mc2SizeMapData => ({
  ...src,
  points: src.points.map((pt) => ({
    ...pt,
    nameTo: pt.nameTo.toUpperCase(),
  })),
});
